import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Marker, TileLayer } from "react-leaflet";
import LeafletMap from "components/LeafletMap";
import MapUtils from "services/map/utils";
import MapConfig from "services/map/MapConfig";
import IndicatorsActioner from "services/indicators/actioner";
import IndicatorsUtils from "services/indicators/utils";
import CommonUtils from "services/common/utils";
import { divIcon, point as leafletPoint } from "leaflet";

const styles = {
  container: {
    width: "100%",
    height: "100%",
  },
  marker: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "40px",
    backgroundColor: IndicatorsUtils.INDICATORS_COLORS.MARKER,
    color: "#fff",
    fontSize: "10px",
  },
};

const MapComponent = ({
  indicators,
  zoom,
  selectedColumn,
  selectedIndicators,
  setConfigParam,
  center,
  minZoom,
  maxZoom,
  tilesUrl,
  handleOnLoad,
  hoveredIndicator,
  leafletMap,
  setLeafletMap,
}) => {
  const handleZoomEnd = (e) => {
    setConfigParam("currentZoom", e.target.getZoom());
  };

  const handleMoveEnd = (e) => {
    setConfigParam("currentPosition", e.target.getCenter());
  };

  const points = useMemo(() => {
    return selectedIndicators
      .map((selectedIndicator, index) => {
        if (selectedIndicator.checked) {
          const areasPoints = indicators[index].items
            .map((area) => {
              return [
                {
                  location: Object.values(
                    area[IndicatorsUtils.INDICATORS_COLUMNS[selectedColumn]]
                      ?.location ?? {}
                  ).reverse(),
                  value:
                    area[IndicatorsUtils.INDICATORS_COLUMNS[selectedColumn]]
                      ?.result || 0,
                },
              ];
            })
            .flat(2);

          if (areasPoints.map((point) => point.location).flat().length === 0) {
            return [];
          }

          return {
            location: MapUtils.getCenterOfPoints(
              areasPoints.map((point) => point.location)
            )?.reverse(),
            value: areasPoints.reduce((acc, area) => acc + area.value, 0),
            uuid: selectedIndicator.uuid,
          };
        }

        return selectedIndicator.areasChecked.map((area) => {
          return {
            location: [
              ...[
                Object.values(
                  indicators[index].items[area.index][
                    IndicatorsUtils.INDICATORS_COLUMNS[selectedColumn]
                  ]?.location ?? {}
                ).reverse() ?? [],
              ],
            ].flat(2),
            value:
              indicators[index].items[area.index][
                IndicatorsUtils.INDICATORS_COLUMNS[selectedColumn]
              ]?.result || 0,
            uuid: area.uuid,
          };
        });
      })
      .flat();
  }, [indicators, selectedColumn, selectedIndicators]);

  const fitBounds = useCallback(() => {
    const bounds = points
      .map((point) => {
        if (point.location.length === 0) {
          return null;
        }
        return point.location;
      })
      .filter((point) => point !== null);

    if (Array.isArray(bounds) && bounds.length > 0) {
      leafletMap?.leafletElement.fitBounds(bounds, {
        padding: [50, 50],
      });
    }
  }, [leafletMap, points]);

  useEffect(() => {
    fitBounds();
  }, [fitBounds, selectedIndicators]);

  const getPointRadius = useCallback(
    (pointValue) => {
      const biggestValue = Math.max(
        ...points.map((point) => Number(point.value)),
        pointValue
      );
      const maxRadius = 30;
      const minRadius = 16;

      return (
        minRadius + (maxRadius - minRadius) * (pointValue / biggestValue) ||
        minRadius
      );
    },
    [points]
  );

  const getMarkerHTMLElement = useCallback(
    (value, hovered) => {
      const markerHTMLElement = document.createElement("span");

      Object.assign(markerHTMLElement.style, {
        ...styles.marker,
        width: `${getPointRadius(value) * 2}px`,
        height: `${getPointRadius(value) * 2}px`,
        backgroundColor: hovered
          ? IndicatorsUtils.INDICATORS_COLORS.HOVER
          : IndicatorsUtils.INDICATORS_COLORS.MARKER,
        border: hovered
          ? `2px solid ${IndicatorsUtils.INDICATORS_COLORS.HOVER}`
          : `2px solid ${IndicatorsUtils.INDICATORS_COLORS.MARKER_BORDER}`,
      });

      markerHTMLElement.innerHTML = CommonUtils.formatNumber(value);

      return markerHTMLElement;
    },
    [getPointRadius]
  );

  const markers = useMemo(() => {
    return points.map((point) => {
      if (point.location.length === 0) {
        return <></>;
      }

      const icon = divIcon({
        className: "",
        html: getMarkerHTMLElement(
          point.value,
          hoveredIndicator === point.uuid
        ),
        iconSize: leafletPoint(
          getPointRadius(point.value) * 2,
          getPointRadius(point.value) * 2
        ),
      });

      return (
        <Marker
          zIndexOffset={point.uuid === hoveredIndicator ? 1 : point.value * -1}
          key={point.uuid}
          icon={icon}
          position={point.location}
        ></Marker>
      );
    });
  }, [getMarkerHTMLElement, getPointRadius, hoveredIndicator, points]);

  return (
    <LeafletMap
      setMapRef={setLeafletMap}
      preferCanvas
      style={styles.container}
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      onZoomEnd={handleZoomEnd}
      onMoveEnd={handleMoveEnd}
    >
      <TileLayer url={MapConfig.defaultTileUrl} />
      <TileLayer
        url={tilesUrl}
        onLoad={handleOnLoad}
        minZoom={minZoom}
        maxZoom={maxZoom}
      />
      {markers}
    </LeafletMap>
  );
};

Map.defaultProps = {};

Map.propTypes = {
  center: PropTypes.array.isRequired,
  zoom: PropTypes.number.isRequired,
  minZoom: PropTypes.number.isRequired,
  maxZoom: PropTypes.number.isRequired,
  parkedCars: PropTypes.object,
  availableParking: PropTypes.object,
  tilesUrl: PropTypes.string,
};

const mapStateToProps = (state) => ({
  center: MapUtils.getCenter(state.Map),
  zoom: MapUtils.getZoom(state.Indicators),
  minZoom: MapUtils.getMinZoom(state.Indicators),
  maxZoom: MapUtils.getMaxZoom(state.Indicators),
  tilesUrl: MapUtils.getTilesUrl(state.Indicators),
});

const mapDispatchToProps = (dispatch) => ({
  setConfigParam: (key, value) =>
    dispatch(IndicatorsActioner.setConfigParam(key, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MapComponent);
